// MIDAS V3 — History page context (Phase 5; trimmed MIDAS-V3-BUGS-0519)
//
// Tiny React context bridging MidasV3Layout ↔ MidasHistoryPageV3 for the
// Ctrl+K focus-search hand-back. The shared "search input ref" pattern lives
// here so the layout's global keydown handler can focus the in-page input
// without the layout knowing what kind of input is on the page.
//
// MIDAS-V3-BUGS-0519 (2026-05-19) — sub-task 1 removed the `+ Nový chat`
// button from the history page header. With the button gone, the context
// no longer needs to expose `openNewChatDialog` — the rail is the sole
// entry point to the New Chat dialog. We keep the context for the Ctrl+K
// search ref hand-back, which is still page-owned.
//
// Why a context (and not a callback prop): React Router's <Outlet /> doesn't
// accept props, and lifting the ref into Redux for one keyboard shortcut is
// overkill. The context is created inside MidasV3Layout and consumed by
// MidasHistoryPageV3 on mount.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import { createContext, MutableRefObject } from "react";

export interface MidasV3HistoryPageContextValue {
  /**
   * The history page mounts and assigns its search input ref into this
   * mutable container so the layout's Ctrl+K handler can focus it without
   * the layout knowing what kind of input is on the page.
   *
   * The history page sets `ref.current = inputEl` on mount and clears it on
   * unmount. The layout reads `ref.current` lazily inside its keydown
   * handler — if null (history page not mounted), the handler falls back to
   * navigation (push `/midas-v3/history`).
   */
  searchInputRef: MutableRefObject<HTMLInputElement | null>;
}

export const MidasV3HistoryPageContext =
  createContext<MidasV3HistoryPageContextValue | null>(null);
